import {
  Box,
  ButtonBase,
  CircularProgress,
  Fade,
  Typography,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { useTelegram } from "../../hooks";
import { WebBackButton } from "../../components/shared";
import {
  cancelSbpPayment,
  confirmSbpPayment,
  getSbpPayment,
  SbpPayment,
  SbpPaymentStatus,
} from "../../services/payments";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

const TERMINAL: SbpPaymentStatus[] = ["SUCCEEDED", "FAILED", "CANCELLED"];

export function PayQrPendingPage() {
  const { paymentId } = useParams<{ paymentId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { tg } = useTelegram();
  const { enqueueSnackbar } = useSnackbar();
  const qc = useQueryClient();
  const { t, i18n } = useTranslation("payqr");
  const numberLocale = i18n.language?.startsWith("en") ? "en-US" : "ru-RU";

  // Куда возвращать юзера при закрытии экрана.
  // Если пришли из истории — обратно в историю; иначе — на главную.
  // Источник передаётся через `location.state.from` из HistoryRow.
  const fromHistory =
    (location.state as { from?: string } | null)?.from === "history";
  const returnPath = fromHistory ? "/history" : "/";

  useEffect(() => {
    const handleBack = () => navigate(returnPath);
    tg.BackButton.show();
    tg.onEvent("backButtonClicked", handleBack);
    return () => {
      tg.BackButton.hide();
      tg.offEvent("backButtonClicked", handleBack);
    };
  }, [navigate, returnPath, tg]);

  const { data, isLoading } = useQuery<SbpPayment>({
    queryKey: ["sbp-payment", paymentId],
    queryFn: () => getSbpPayment(paymentId!),
    enabled: Boolean(paymentId),
    // Активно поллим, пока задача не подтверждена юзером или не терминальная.
    // AWAITING_CONFIRMATION тоже поллим — сумма может обновиться от воркера
    // (например, если банк уточнил её после первичного показа).
    refetchInterval: (payment) => {
      if (!payment) return 2500;
      if (TERMINAL.includes(payment.status)) return false;
      return 2500;
    },
  });

  const { mutate: cancel, isLoading: cancelling } = useMutation(
    () => cancelSbpPayment(paymentId!),
    {
      onSuccess: (updated) => {
        qc.setQueryData(["sbp-payment", paymentId], updated);
      },
      onError: () => {
        enqueueSnackbar(t("errors.cancel_failed"), { variant: "error" });
      },
    },
  );

  const { mutate: confirm, isLoading: confirming } = useMutation(
    () => confirmSbpPayment(paymentId!),
    {
      onSuccess: (updated) => {
        qc.setQueryData(["sbp-payment", paymentId], updated);
      },
      onError: (err: unknown) => {
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data
            ?.message ?? t("errors.confirm_failed");
        enqueueSnackbar(msg, { variant: "error" });
      },
    },
  );

  if (isLoading || !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", marginTop: "60px" }}>
        <CircularProgress />
      </Box>
    );
  }

  const isPreview = data.status === "AWAITING_CONFIRMATION";
  const isActive = data.status === "PENDING" || data.status === "IN_PROGRESS";
  const isTerminal = TERMINAL.includes(data.status);

  return (
    <Fade in>
      <Box
        sx={{
          width: "100%",
          paddingTop: "20px",
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 220px)",
        }}
      >
        <WebBackButton />
        {isPreview ? (
          <PreviewHero payment={data} t={t} numberLocale={numberLocale} />
        ) : (
          <StatusHero payment={data} t={t} />
        )}

        <Box sx={{ marginTop: "26px", display: "grid", gap: "10px" }}>
          {isPreview ? (
            <PreviewRows payment={data} t={t} />
          ) : (
            <>
              {/* Получатель — сохранён payer'ом в meta.recipient. Показываем
                  на всех терминальных/процессных экранах, чтобы юзер видел,
                  кому ушёл платёж. */}
              {data.recipient && (
                <InfoRow label={t("pending.recipient")} value={data.recipient} />
              )}
              {data.amount && data.currency && (
                <InfoRow
                  label={t("pending.amount")}
                  value={`${Number(data.amount).toLocaleString(numberLocale)} ${data.currency}`}
                />
              )}
              {data.usdt_amount && data.status === "SUCCEEDED" && (
                <InfoRow
                  label={t("pending.amount_debited")}
                  value={`${Number(data.usdt_amount).toLocaleString(numberLocale, {
                    maximumFractionDigits: 2,
                  })} USDT`}
                />
              )}
              {data.external_ref && (
                <InfoRow
                  label={t("pending.operation_number")}
                  value={data.external_ref}
                />
              )}
              <InfoRow
                label={t("pending.payment_number")}
                value={`ZERO-${data.id}`}
              />
            </>
          )}
        </Box>

        {/* --- Кнопки --- */}
        {isPreview && (
          <>
            <ButtonBase
              onClick={() => confirm()}
              disabled={confirming}
              sx={{
                width: "100%",
                marginTop: "24px",
                padding: "16px",
                borderRadius: "16px",
                bgcolor: "primary.main",
                color: "primary.contrastText",
                fontWeight: 700,
                fontSize: "15px",
                "&:active": { opacity: 0.85 },
                position: "relative",
                zIndex: 100005,
              }}
            >
              {confirming ? (
                <CircularProgress size={18} sx={{ color: "inherit" }} />
              ) : data.amount ? (
                t("pending.pay_amount", {
                  amount: Number(data.amount).toLocaleString(numberLocale),
                })
              ) : (
                t("pending.pay")
              )}
            </ButtonBase>
            <ButtonBase
              onClick={() => cancel()}
              disabled={cancelling}
              sx={(theme) => ({
                width: "100%",
                marginTop: "10px",
                padding: "16px",
                borderRadius: "16px",
                bgcolor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.06)"
                    : "rgba(0, 0, 0, 0.05)",
                color: theme.palette.text.primary,
                fontWeight: 600,
                fontSize: "15px",
                "&:active": { opacity: 0.85 },
                position: "relative",
                zIndex: 100005,
              })}
            >
              {cancelling ? (
                <CircularProgress size={18} sx={{ color: "inherit" }} />
              ) : (
                t("pending.cancel")
              )}
            </ButtonBase>
          </>
        )}

        {isActive && (
          <ButtonBase
            onClick={() => cancel()}
            disabled={cancelling}
            sx={(theme) => ({
              width: "100%",
              marginTop: "24px",
              padding: "16px",
              borderRadius: "16px",
              bgcolor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.06)"
                  : "rgba(0, 0, 0, 0.05)",
              color: theme.palette.text.primary,
              fontWeight: 600,
              fontSize: "15px",
              "&:active": { opacity: 0.85 },
              position: "relative",
              zIndex: 100005,
            })}
          >
            {cancelling ? (
              <CircularProgress size={18} sx={{ color: "inherit" }} />
            ) : (
              t("pending.cancel")
            )}
          </ButtonBase>
        )}

        {isTerminal && (
          <ButtonBase
            onClick={() => navigate(returnPath)}
            sx={{
              width: "100%",
              marginTop: "24px",
              padding: "16px",
              borderRadius: "16px",
              bgcolor: "primary.main",
              color: "primary.contrastText",
              fontWeight: 700,
              fontSize: "15px",
              "&:active": { opacity: 0.85 },
              position: "relative",
              zIndex: 100005,
            }}
          >
            {t("pending.done")}
          </ButtonBase>
        )}
      </Box>
    </Fade>
  );
}

function PreviewHero({
  payment,
  t,
  numberLocale,
}: {
  payment: SbpPayment;
  t: TFunction;
  numberLocale: string;
}) {
  const amountNum = payment.amount ? Number(payment.amount) : null;
  const usdtNum = payment.usdt_estimate ? Number(payment.usdt_estimate) : null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px" }}>
      <Box
        sx={(theme) => ({
          width: 72,
          height: 72,
          borderRadius: "999px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor:
            theme.palette.mode === "dark"
              ? "rgba(51, 119, 255, 0.15)"
              : "rgba(51, 119, 255, 0.1)",
          color: "#3377FF",
        })}
      >
        <Icon icon="solar:qr-code-bold-duotone" width={36} height={36} color="currentColor" />
      </Box>

      <Typography sx={{ fontSize: "14px", color: "text.secondary" }}>
        {t("pending.sbp_payment")}
      </Typography>

      {payment.recipient && (
        <Typography
          sx={{
            fontSize: "15px",
            fontWeight: 600,
            color: "text.primary",
            textAlign: "center",
            padding: "0 16px",
            maxWidth: 320,
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {payment.recipient}
        </Typography>
      )}

      <Typography sx={{ fontSize: "34px", fontWeight: 800, lineHeight: 1.1 }}>
        {amountNum !== null
          ? `${amountNum.toLocaleString(numberLocale)} ₽`
          : "—"}
      </Typography>

      {usdtNum !== null && (
        <Typography sx={{ fontSize: "14px", color: "text.secondary" }}>
          ≈{" "}
          {usdtNum.toLocaleString(numberLocale, { maximumFractionDigits: 2 })}{" "}
          USDT
          {payment.rate
            ? ` • ${t("pending.rate", {
                rate: Number(payment.rate).toLocaleString(numberLocale, {
                  maximumFractionDigits: 2,
                }),
              })}`
            : ""}
        </Typography>
      )}
    </Box>
  );
}

function PreviewRows({
  payment,
  t,
}: {
  payment: SbpPayment;
  t: TFunction;
}) {
  const usdtBase = payment.usdt_amount ? Number(payment.usdt_amount) : null;
  const fee = payment.fee_amount ? Number(payment.fee_amount) : null;
  const total = payment.usdt_estimate ? Number(payment.usdt_estimate) : null;
  const hasFee = fee !== null && fee > 0;

  const fmt = (v: number) =>
    `${v.toLocaleString("ru-RU", { maximumFractionDigits: 2 })} USDT`;

  return (
    <>
      {payment.recipient && (
        <InfoRow label={t("pending.recipient")} value={payment.recipient} />
      )}

      {/*
        Логика вывода USDT-строк:
        • Если есть комиссия — показываем разбивку:
            «К списанию» (чистая USDT) + «Комиссия» + «Итого с баланса».
        • Если комиссии нет — К списанию и Итого равны, поэтому оставляем
          только одну строку «Спишется с баланса».
      */}
      {hasFee ? (
        <>
          {usdtBase !== null && (
            <InfoRow label={t("pending.to_debit")} value={fmt(usdtBase)} />
          )}
          <InfoRow label={t("pending.commission")} value={fmt(fee!)} />
          {total !== null && (
            <InfoRow
              label={t("pending.total_from_balance")}
              value={fmt(total)}
            />
          )}
        </>
      ) : (
        (total ?? usdtBase) !== null && (
          <InfoRow
            label={t("pending.debit_from_balance")}
            value={fmt((total ?? usdtBase)!)}
          />
        )
      )}

      <InfoRow
        label={t("pending.payment_number")}
        value={`ZERO-${payment.id}`}
      />
    </>
  );
}

function StatusHero({
  payment,
  t,
}: {
  payment: SbpPayment;
  t: TFunction;
}) {
  const meta = getStatusMeta(payment.status, t);
  const showSpinner =
    payment.status === "PENDING" || payment.status === "IN_PROGRESS";
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "14px" }}>
      <Box sx={{ position: "relative", width: 96, height: 96 }}>
        <Box
          sx={(theme) => ({
            position: "absolute",
            inset: 0,
            borderRadius: "999px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.06)"
                : "rgba(0, 0, 0, 0.05)",
            color: meta.color,
          })}
        >
          <Icon icon={meta.icon} width={44} height={44} color="currentColor" />
        </Box>
        {showSpinner && (
          <CircularProgress
            size={96}
            thickness={2.2}
            sx={{ position: "absolute", inset: 0, color: meta.color }}
          />
        )}
      </Box>
      <Typography sx={{ fontSize: "22px", fontWeight: 700 }}>{meta.title}</Typography>
      <Typography
        sx={{
          fontSize: "13px",
          color: "text.secondary",
          textAlign: "center",
          padding: "0 20px",
          maxWidth: 320,
        }}
      >
        {payment.error_message ?? meta.subtitle}
      </Typography>
    </Box>
  );
}

function InfoRow({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: string;
}) {
  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "14px 16px",
        borderRadius: "16px",
        bgcolor: theme.palette.secondary.main,
        border:
          theme.palette.mode === "light"
            ? "1px solid rgba(0, 0, 0, 0.06)"
            : "none",
      })}
    >
      <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>{label}</Typography>
      <Typography
        sx={{
          fontSize: "14px",
          fontWeight: 700,
          color: accent ?? "text.primary",
          maxWidth: "60%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function getStatusMeta(
  status: SbpPaymentStatus,
  t: TFunction,
): {
  title: string;
  subtitle: string;
  icon: string;
  color: string;
} {
  switch (status) {
    case "AWAITING_CONFIRMATION":
      return {
        title: t("pending.statuses.awaiting.title"),
        subtitle: t("pending.statuses.awaiting.subtitle"),
        icon: "solar:qr-code-bold-duotone",
        color: "#3377FF",
      };
    case "PENDING":
    case "IN_PROGRESS":
      return {
        title: t("pending.statuses.processing.title"),
        subtitle: t("pending.statuses.processing.subtitle"),
        icon: "solar:refresh-bold-duotone",
        color: "#3377FF",
      };
    case "SUCCEEDED":
      return {
        title: t("pending.statuses.succeeded.title"),
        subtitle: t("pending.statuses.succeeded.subtitle"),
        icon: "solar:check-circle-bold",
        color: "#22C55E",
      };
    case "FAILED":
      return {
        title: t("pending.statuses.failed.title"),
        subtitle: t("pending.statuses.failed.subtitle"),
        icon: "solar:close-circle-bold",
        color: "#EF4444",
      };
    case "CANCELLED":
      return {
        title: t("pending.statuses.cancelled.title"),
        subtitle: t("pending.statuses.cancelled.subtitle"),
        icon: "solar:close-square-bold-duotone",
        color: "#EF4444",
      };
  }
}
